/**
 * Crisp Chat Helper Functions
 * 
 * Utilities for interacting with the Crisp chat widget,
 * including pre-filled messages for different feedback types.
 */

export type FeedbackType = 'cancellation' | 'downgrade' | 'general';

/**
 * Open Crisp chat with a pre-filled message based on feedback type
 */
export const openCrispWithFeedback = (feedbackType: FeedbackType) => {
  if (!window.$crisp) {
    console.warn('[Crisp] Chat not initialized');
    return;
  }

  // Open chat
  window.$crisp.push(['do', 'chat:open']);

  // Pre-fill message based on type
  const messages: Record<FeedbackType, string> = {
    cancellation: "Hi, I recently cancelled my subscription and wanted to provide feedback about my experience...",
    downgrade: "Hi, I recently downgraded my plan and wanted to share feedback about why...",
    general: "Hi, I'd like to provide feedback about BotBlender..."
  };

  // Set pre-filled message
  window.$crisp.push(['set', 'message:text', [messages[feedbackType]]]);

  // Tag conversation
  window.$crisp.push(['set', 'session:data', [[
    ['feedback_type', feedbackType],
    ['feedback_timestamp', new Date().toISOString()]
  ]]]);

  console.log(`[Crisp] Opened chat with ${feedbackType} feedback template`);
};

/**
 * Open Crisp chat for general support
 */
export const openCrispForSupport = () => {
  if (!window.$crisp) {
    console.warn('[Crisp] Chat not initialized');
    return;
  }
  window.$crisp.push(['do', 'chat:open']);
  console.log('[Crisp] Opened chat for support');
};

/**
 * Navigate to a specific onboarding step from Crisp picker
 * Uses custom events to communicate with UniversalSetupOnboarding component
 */
export const navigateToOnboardingStep = (stepValue: string) => {
  const stepMap: Record<string, number> = {
    'welcome': 1,
    'accounts': 2,
    'strategies': 3,
    'completion': 4,
  };
  
  const stepId = stepMap[stepValue];
  if (!stepId) {
    console.warn('[Crisp] Unknown step value:', stepValue);
    return;
  }
  
  // Check if user is on onboarding page
  const isOnOnboarding = window.location.pathname === '/onboarding';
  
  if (isOnOnboarding) {
    // Already on onboarding - just navigate to step
    window.dispatchEvent(new CustomEvent('crisp-navigate-step', { 
      detail: { stepId } 
    }));
    
    // Show feedback in Crisp
    if (window.$crisp) {
      const stepNames: Record<number, string> = {
        1: 'Welcome',
        2: 'Connect Accounts',
        3: 'Define Strategies',
        4: 'Complete Setup'
      };
      window.$crisp.push(['do', 'message:show', ['text', 
        `📍 Navigating to ${stepNames[stepId]}...`
      ]]);
    }
  } else {
    // On Dashboard or other page - navigate to onboarding first
    window.location.href = '/onboarding?fromCrisp=true';
    
    // Store target step in sessionStorage for pickup after navigation
    sessionStorage.setItem('crisp-target-step', stepId.toString());
  }
};
